// src/components/dashboard/PeriodComparison.tsx
import React from 'react';
import { ArrowUpIcon, ArrowDownIcon, CalendarIcon } from 'lucide-react';
import {
  formatPercentage
} from '../utils/formatters.ts';
import { useTheme } from '../contexts/ThemeContext.tsx';

type PeriodData = {
  leads: number;
  agendamentos: number;
  comparecimentos: number;
  vendas: number;
};

type PeriodComparisonProps = {
  mesAtual: PeriodData;
  mesAnterior: PeriodData;
  periodoLabel: string;
  dataAtual: string;
};

export const PeriodComparison: React.FC<PeriodComparisonProps> = ({
  mesAtual,
  mesAnterior,
  periodoLabel,
  dataAtual,
}) => {
  const { isDark } = useTheme();

  const buildComparisonMetric = (label: string, atual: number, anterior: number) => {
    const hasPreviousBase = anterior > 0;
    const percentChange = hasPreviousBase
      ? ((atual - anterior) / anterior) * 100
      : 0;
    const deltaType: 'increase' | 'decrease' | 'unchanged' = !hasPreviousBase
      ? (atual > 0 ? 'increase' : 'unchanged')
      : (percentChange > 0 ? 'increase' : percentChange < 0 ? 'decrease' : 'unchanged');
    const badgeLabel = !hasPreviousBase
      ? (atual > 0 ? 'Novo' : '0%')
      : formatPercentage(percentChange);
    const progressPercent = hasPreviousBase
      ? Math.min(100, (atual / anterior) * 100)
      : (atual > 0 ? 100 : 0);
    const progressValueLabel = hasPreviousBase
      ? `${Math.round((atual / anterior) * 100)}%`
      : (atual > 0 ? 'Nova base' : '0%');

    return {
      label,
      atual,
      anterior,
      deltaType,
      badgeLabel,
      progressPercent,
      progressValueLabel,
      progressTitle: hasPreviousBase ? 'Atual sobre anterior' : 'Base comparativa',
    };
  };

  const metrics = [
    buildComparisonMetric('Leads', mesAtual.leads, mesAnterior.leads),
    buildComparisonMetric('Agendamentos', mesAtual.agendamentos, mesAnterior.agendamentos),
    buildComparisonMetric('Comparecimentos', mesAtual.comparecimentos, mesAnterior.comparecimentos),
    buildComparisonMetric('Vendas', mesAtual.vendas, mesAnterior.vendas),
  ];

  return (
    <div className={`rounded-lg shadow-sm border mb-6 ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
      {/* Cabeçalho */}
      <div className={`p-4 border-b ${isDark ? 'border-gray-700' : ''}`}>
        <div className="flex justify-between items-center">
          <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>{periodoLabel}</h3>
          <div className={`flex items-center gap-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
            <CalendarIcon className="h-4 w-4" />
            <span className="text-sm">Hoje: {dataAtual}</span>
          </div>
        </div>
      </div>

      {/* Cards de Métricas */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 p-4">
        {metrics.map((metric) => (
          <div
            key={metric.label}
            className={`rounded-2xl border p-5 transition-all hover:shadow-md ${isDark
                ? 'bg-gray-800 border-gray-700'
                : 'bg-white border-gray-200'
              }`}
          >
            <div className="flex justify-between items-start mb-3">
              <div>
                <span className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{metric.label}</span>
                <div className={`text-3xl font-bold mt-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>{metric.atual}</div>
              </div>

              <div className={`flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${metric.deltaType === 'increase'
                  ? (isDark ? 'bg-emerald-500/10 text-emerald-400' : 'bg-emerald-50 text-emerald-600')
                  : metric.deltaType === 'decrease'
                    ? (isDark ? 'bg-rose-500/10 text-rose-400' : 'bg-rose-50 text-rose-600')
                    : (isDark ? 'bg-gray-700 text-gray-400' : 'bg-gray-100 text-gray-500')
                }`}>
                {metric.deltaType === 'increase' && <ArrowUpIcon className="h-3 w-3 mr-1" />}
                {metric.deltaType === 'decrease' && <ArrowDownIcon className="h-3 w-3 mr-1" />}
                {metric.badgeLabel}
              </div>
            </div>

            <div className={`text-xs mb-4 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
              vs. {metric.anterior} (mês anterior)
            </div>

            <div className="flex justify-between text-xs mb-1.5 font-medium">
              <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>{metric.progressTitle}</span>
              <span className="text-sminy-primary">{metric.progressValueLabel}</span>
            </div>

            <div className={`h-1.5 rounded-full overflow-hidden ${isDark ? 'bg-gray-700' : 'bg-gray-100'}`}>
              <div
                className="h-full rounded-full bg-sminy-primary"
                style={{ width: `${metric.progressPercent}%` }}
              ></div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default PeriodComparison;
